/**
 * Cookie consent helpers for Google Consent Mode v2 + Thai PDPA.
 */
export const CONSENT_STORAGE_KEY = "vx_cookie_consent_v1"
export const CONSENT_VERSION = 1

export type ConsentCategories = {
  necessary: true
  analytics: boolean
  marketing: boolean
  preferences: boolean
}

export type StoredConsent = {
  version: number
  updatedAt: string
  categories: ConsentCategories
}

export const DEFAULT_DENIED: ConsentCategories = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false,
}

export const ALL_GRANTED: ConsentCategories = {
  necessary: true,
  analytics: true,
  marketing: true,
  preferences: true,
}

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

export function ensureGtag(): void {
  if (typeof window === "undefined") {
    return
  }
  window.dataLayer = window.dataLayer || []
  if (!window.gtag) {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer?.push(args)
    }
  }
}

export function toGoogleConsent(categories: ConsentCategories) {
  const analytics = categories.analytics ? "granted" : "denied"
  const marketing = categories.marketing ? "granted" : "denied"
  const preferences = categories.preferences ? "granted" : "denied"
  return {
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
    analytics_storage: analytics,
    functionality_storage: preferences,
    personalization_storage: preferences,
    security_storage: "granted" as const,
  }
}

export function applyGoogleConsent(categories: ConsentCategories): void {
  if (typeof window === "undefined") {
    return
  }
  ensureGtag()
  window.gtag?.("consent", "update", toGoogleConsent(categories))
  window.dataLayer?.push({
    event: "cookie_consent_update",
    cookie_consent: categories,
  })
}

export function readStoredConsent(): StoredConsent | null {
  if (typeof window === "undefined") {
    return null
  }
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as StoredConsent
    if (!parsed?.categories || parsed.version !== CONSENT_VERSION) {
      return null
    }
    return {
      ...parsed,
      categories: {
        necessary: true,
        analytics: !!parsed.categories.analytics,
        marketing: !!parsed.categories.marketing,
        preferences: !!parsed.categories.preferences,
      },
    }
  } catch {
    return null
  }
}

export function writeStoredConsent(categories: ConsentCategories): StoredConsent {
  const payload: StoredConsent = {
    version: CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
    categories: {
      necessary: true,
      analytics: !!categories.analytics,
      marketing: !!categories.marketing,
      preferences: !!categories.preferences,
    },
  }
  if (typeof window !== "undefined") {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(payload))
  }
  applyGoogleConsent(payload.categories)
  return payload
}

export function openConsentSettings(): void {
  if (typeof window === "undefined") {
    return
  }
  window.dispatchEvent(new CustomEvent("vx:open-cookie-settings"))
}